export const DONGCHA_PAYMENT_SOURCE = 'dongcha'
export const DONGCHA_PAYMENT_CONTEXT_KEY = 'qx-dongcha-payment-context:v1'
const DONGCHA_CHANNEL_ENTER_TRACKED_KEY = 'qx-dongcha-channel-enter-tracked:v1'

const CONTEXT_VERSION = 1
const CONTEXT_TTL_MS = 30 * 60 * 1000
const TAB_BINDING_PREFIX = 'qx-dongcha-payment:'
const PAYMENT_ENTRY_SOURCES = new Set([1, 2])
const WECOM_AFTER_ADD_KEYS = ['qx-wecom-after-add-pending', 'qx-wecom-after-add-due-time']

export type DongchaPaymentEntrySource = 1 | 2
export type DongchaChannelEntrySource = DongchaPaymentEntrySource | 0 | 'unknown'
export type DongchaChannelUserIdentity = 'guest' | 'linked_formal'
export type DongchaPaymentNavigationBoundary =
  | 'external-document'
  | 'spa-navigation'
  | 'document-reload'
  | 'trusted-login-reload'

export interface DongchaPaymentEntryPresence {
  source: boolean
  entrySource: boolean
  returnUrl: boolean
}

export interface DongchaPaymentEntry {
  source: string
  entrySource: string
  returnUrl: string
  presence: DongchaPaymentEntryPresence
}

export interface DongchaPaymentContext {
  version: 1
  entryId: string
  tabId: string
  source: 'dongcha'
  entrySource: DongchaPaymentEntrySource
  returnUrl: string
  createdAt: number
  guestEnterTracked: boolean
  closeTracked: boolean
  closeInProgress: boolean
}

interface StorageLike {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

interface TabBindingLike {
  get: () => string
  bind: (tabId: string) => boolean
  clear: () => void
}

interface ContextOptions {
  storage?: StorageLike | null
  tabBinding?: TabBindingLike | null
  now?: number
  preserveExistingContextOnExternalNavigation?: boolean
}

export type DongchaPaymentContextState =
  | { status: 'active'; context: DongchaPaymentContext }
  | { status: 'invalid' | 'none'; context: null }

export type DongchaPaymentCloseAttempt = {
  context: DongchaPaymentContext
  shouldTrack: boolean
}

const getSessionStorage = (): StorageLike | null => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

const browserTabBinding: TabBindingLike = {
  get: () => {
    try {
      if (typeof window === 'undefined' || !window.name.startsWith(TAB_BINDING_PREFIX)) return ''
      return window.name.slice(TAB_BINDING_PREFIX.length)
    } catch {
      return ''
    }
  },
  bind: (tabId) => {
    try {
      if (typeof window === 'undefined') return false
      window.name = `${TAB_BINDING_PREFIX}${tabId}`
      return window.name === `${TAB_BINDING_PREFIX}${tabId}`
    } catch {
      return false
    }
  },
  clear: () => {
    try {
      if (typeof window !== 'undefined' && window.name.startsWith(TAB_BINDING_PREFIX)) window.name = ''
    } catch {
      // A missing tab binding already fails closed.
    }
  },
}

const getTabBinding = (tabBinding?: TabBindingLike | null) =>
  tabBinding === undefined ? browserTabBinding : tabBinding

/** Return the query-decoded value without imposing an origin, credential, or length policy. */
export const validateDecodedReturnUrl = (value: string): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null

/** `getSearchParam` already performs the single outer query decode. Never Base64-decode or recurse. */
export const decodeAndValidateReturnUrl = (value: string): string | null =>
  validateDecodedReturnUrl(value)

const createEntryId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const parseEntrySource = (value: string): DongchaChannelEntrySource => {
  if (!/^\d+$/.test(value)) return value ? 'unknown' : 0
  const parsed = Number(value)
  return Number.isSafeInteger(parsed) && PAYMENT_ENTRY_SOURCES.has(parsed)
    ? parsed as DongchaPaymentEntrySource
    : parsed === 0 ? 0 : 'unknown'
}

const isContextShapeValid = (value: unknown): value is DongchaPaymentContext => {
  if (!value || typeof value !== 'object') return false
  const context = value as Partial<DongchaPaymentContext>
  return (
    context.version === CONTEXT_VERSION &&
    typeof context.entryId === 'string' && context.entryId.length > 0 &&
    context.tabId === context.entryId && context.source === DONGCHA_PAYMENT_SOURCE &&
    PAYMENT_ENTRY_SOURCES.has(context.entrySource as number) &&
    typeof context.returnUrl === 'string' && context.returnUrl.length > 0 &&
    typeof context.createdAt === 'number' &&
    typeof context.guestEnterTracked === 'boolean' &&
    typeof context.closeTracked === 'boolean' &&
    typeof context.closeInProgress === 'boolean'
  )
}

export const clearDongchaPaymentContext = (
  storage = getSessionStorage(),
  tabBinding: TabBindingLike | null = browserTabBinding,
) => {
  try {
    storage?.removeItem(DONGCHA_PAYMENT_CONTEXT_KEY)
    storage?.removeItem(DONGCHA_CHANNEL_ENTER_TRACKED_KEY)
  } catch {
    // Storage failure must not interrupt the existing Dongcha flow.
  }
  tabBinding?.clear()
}

export const getDongchaPaymentContextState = (options: ContextOptions = {}): DongchaPaymentContextState => {
  const storage = options.storage === undefined ? getSessionStorage() : options.storage
  const tabBinding = getTabBinding(options.tabBinding)
  if (!storage || !tabBinding) return { status: 'none', context: null }
  let raw: string | null
  try { raw = storage.getItem(DONGCHA_PAYMENT_CONTEXT_KEY) } catch { return { status: 'none', context: null } }
  if (!raw) return { status: 'none', context: null }
  try {
    const context: unknown = JSON.parse(raw)
    const now = options.now ?? Date.now()
    if (!isContextShapeValid(context) || tabBinding.get() !== context.tabId || now < context.createdAt || now - context.createdAt > CONTEXT_TTL_MS) {
      clearDongchaPaymentContext(storage, tabBinding)
      return { status: 'invalid', context: null }
    }
    return { status: 'active', context }
  } catch {
    clearDongchaPaymentContext(storage, tabBinding)
    return { status: 'invalid', context: null }
  }
}

export const getActiveDongchaPaymentContext = (options: ContextOptions = {}) => {
  const state = getDongchaPaymentContextState(options)
  return state.status === 'active' ? state.context : null
}

export const resolveDongchaPaymentNavigationBoundary = ({ hasPreviousRoute, isDocumentReload, hasTrustedLoginReload }: {
  hasPreviousRoute: boolean
  isDocumentReload: boolean
  hasTrustedLoginReload: boolean
}): DongchaPaymentNavigationBoundary => {
  if (hasPreviousRoute) return 'spa-navigation'
  if (isDocumentReload) return 'document-reload'
  if (hasTrustedLoginReload) return 'trusted-login-reload'
  return 'external-document'
}

const hasCompletePaymentEntry = (entry: DongchaPaymentEntry) =>
  entry.presence.source && entry.presence.entrySource && entry.presence.returnUrl

const hasAnyPaymentContractField = (presence: DongchaPaymentEntryPresence) =>
  presence.entrySource || presence.returnUrl

export const reconcileDongchaPaymentEntry = (
  entry: DongchaPaymentEntry,
  boundary: DongchaPaymentNavigationBoundary,
  options: ContextOptions = {},
) => {
  const storage = options.storage === undefined ? getSessionStorage() : options.storage
  const tabBinding = getTabBinding(options.tabBinding)
  if (!storage || !tabBinding) return null
  const parsedSource = parseEntrySource(entry.entrySource)
  if (hasCompletePaymentEntry(entry) && entry.source.toLowerCase() === DONGCHA_PAYMENT_SOURCE && PAYMENT_ENTRY_SOURCES.has(parsedSource as number)) {
    const returnUrl = decodeAndValidateReturnUrl(entry.returnUrl)
    if (!returnUrl) {
      clearDongchaPaymentContext(storage, tabBinding)
      return null
    }
    const entryId = createEntryId()
    if (!tabBinding.bind(entryId)) {
      clearDongchaPaymentContext(storage, tabBinding)
      return null
    }
    const context: DongchaPaymentContext = {
      version: CONTEXT_VERSION,
      entryId,
      tabId: entryId,
      source: DONGCHA_PAYMENT_SOURCE,
      entrySource: parsedSource as DongchaPaymentEntrySource,
      returnUrl,
      createdAt: options.now ?? Date.now(),
      guestEnterTracked: false,
      closeTracked: false,
      closeInProgress: false,
    }
    try {
      storage.setItem(DONGCHA_PAYMENT_CONTEXT_KEY, JSON.stringify(context))
      clearWeComAfterAddMarkers(storage)
      return context
    } catch {
      clearDongchaPaymentContext(storage, tabBinding)
      return null
    }
  }
  if (
    hasAnyPaymentContractField(entry.presence) ||
    boundary === 'document-reload' ||
    (boundary === 'external-document' && !options.preserveExistingContextOnExternalNavigation)
  ) {
    clearDongchaPaymentContext(storage, tabBinding)
    return null
  }
  return getActiveDongchaPaymentContext({ ...options, storage, tabBinding })
}

const updateContext = (updater: (context: DongchaPaymentContext) => DongchaPaymentContext | null, options: ContextOptions = {}) => {
  const storage = options.storage === undefined ? getSessionStorage() : options.storage
  const tabBinding = getTabBinding(options.tabBinding)
  if (!storage || !tabBinding) return null
  const context = getActiveDongchaPaymentContext({ ...options, storage, tabBinding })
  if (!context) return null
  const updated = updater(context)
  if (!updated) return null
  try { storage.setItem(DONGCHA_PAYMENT_CONTEXT_KEY, JSON.stringify(updated)); return updated } catch { return null }
}

export const markDongchaPaymentGuestEnterTracked = (options: ContextOptions = {}) =>
  updateContext((context) => context.guestEnterTracked ? null : { ...context, guestEnterTracked: true }, options)

export const markDongchaChannelEnterTracked = (userIdentity: DongchaChannelUserIdentity, options: ContextOptions = {}) => {
  const storage = options.storage === undefined ? getSessionStorage() : options.storage
  const activePaymentContext = getActiveDongchaPaymentContext(options)
  if (activePaymentContext) {
    const paymentContext = markDongchaPaymentGuestEnterTracked(options)
    return paymentContext ? { paymentContext, userIdentity } : null
  }
  if (!storage) return null
  try {
    if (storage.getItem(DONGCHA_CHANNEL_ENTER_TRACKED_KEY)) return null
    storage.setItem(DONGCHA_CHANNEL_ENTER_TRACKED_KEY, '1')
    return { paymentContext: null, userIdentity }
  } catch { return null }
}

export const beginDongchaPaymentClose = (options: ContextOptions = {}): DongchaPaymentCloseAttempt | null => {
  const current = getActiveDongchaPaymentContext(options)
  if (!current || current.closeInProgress) return null
  const context = updateContext((active) => ({ ...active, closeTracked: true, closeInProgress: true }), options)
  return context ? { context, shouldTrack: !current.closeTracked } : null
}

export const rollbackDongchaPaymentClose = (entryId: string, options: ContextOptions = {}) =>
  updateContext((context) => context.entryId === entryId ? { ...context, closeInProgress: false } : null, options)

export const clearWeComAfterAddMarkers = (storage = getSessionStorage()) => {
  try { WECOM_AFTER_ADD_KEYS.forEach((key) => storage?.removeItem(key)) } catch { /* best effort */ }
}

export const shouldBypassWeComRetention = (source: string, options: ContextOptions = {}) =>
  source.toLowerCase() === DONGCHA_PAYMENT_SOURCE && Boolean(getActiveDongchaPaymentContext(options))

export const shouldSuppressWeComAfterAdd = (source: string, appEnvironment: boolean, options: ContextOptions = {}) =>
  !appEnvironment && shouldBypassWeComRetention(source, options)

export const buildDongchaPaymentTrackParams = (context: DongchaPaymentContext) => ({
  channel: context.source,
  entry_path: 'payment',
  entry_source: context.entrySource,
})

export const buildDongchaPageCloseTrackParams = (
  paymentContext: DongchaPaymentContext | null,
  userIdentity: DongchaChannelUserIdentity,
) => paymentContext
  ? { ...buildDongchaPaymentTrackParams(paymentContext), user_identity: userIdentity }
  : {
      channel: DONGCHA_PAYMENT_SOURCE,
      user_identity: userIdentity,
      entry_path: 'other',
      entry_source: 0,
    }

export const buildDongchaChannelEnterTrackParams = (
  userIdentity: DongchaChannelUserIdentity,
  paymentContext: DongchaPaymentContext | null,
  entrySource: DongchaChannelEntrySource = paymentContext?.entrySource || 0,
) => ({
  channel: DONGCHA_PAYMENT_SOURCE,
  user_identity: userIdentity,
  entry_path: paymentContext ? 'payment' : 'other',
  entry_source: paymentContext?.entrySource || entrySource,
})
